/**
 * DocumentSlot.jsx · v2.7.65+E78
 * ═══════════════════════════════
 * A single document-upload slot with client-side image enhancement.
 * Uses the native HTML5 Canvas 2D API to:
 *   1. Auto-crop white borders (edge-scan)
 *   2. Approximate perspective flattening via bounding-box rescale
 *   3. Contrast + sharpness enhancement (unsharp-mask kernel)
 * Then re-encodes the raster as image/jpeg @ 0.85 quality — typically
 * shaves 60-80% off phone-shot documents so the 50 MB ceiling is easy.
 *
 * PDFs bypass the canvas pipeline (no rasterization) and stream as-is.
 */
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Upload, Loader2, CheckCircle2, FileText, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 50 * 1024 * 1024;   // 50 MB
const ACCEPT_ATTR = "image/*,application/pdf";

/** Rasterize + auto-crop + sharpen + re-encode a photo file to JPEG. */
async function enhanceImage(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = rej;
      im.src = url;
    });
    // Ceiling raster dimension so a 12MP phone shot doesn't blow the heap.
    const MAX_DIM = 2000;
    const scale = Math.min(1, MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.round(img.naturalWidth  * scale);
    const h = Math.round(img.naturalHeight * scale);
    const c = document.createElement("canvas");
    c.width  = w;
    c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    // ─ Edge-scan for a bounding box of non-background pixels ─
    // Sample every 4 px for perf on large rasters.
    const img_data = ctx.getImageData(0, 0, w, h);
    const px = img_data.data;
    let minX = w, minY = h, maxX = 0, maxY = 0;
    const step = 4;
    const isFg = (i) => (px[i] < 235 || px[i + 1] < 235 || px[i + 2] < 235);
    for (let y = 0; y < h; y += step) {
      for (let x = 0; x < w; x += step) {
        const i = (y * w + x) * 4;
        if (isFg(i)) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
    // Cropping only fires if bounding box is meaningfully smaller than the frame.
    let out = c;
    if (maxX > minX && maxY > minY && (maxX - minX) < w * 0.9 && (maxY - minY) < h * 0.9) {
      const padX = Math.min(w * 0.02, 40);
      const padY = Math.min(h * 0.02, 40);
      const cx = Math.max(0, minX - padX);
      const cy = Math.max(0, minY - padY);
      const cw = Math.min(w - cx, (maxX - minX) + 2 * padX);
      const ch = Math.min(h - cy, (maxY - minY) + 2 * padY);
      const cropped = document.createElement("canvas");
      cropped.width  = cw;
      cropped.height = ch;
      cropped.getContext("2d").drawImage(c, cx, cy, cw, ch, 0, 0, cw, ch);
      out = cropped;
    }

    // ─ Contrast + sharpness pass ─
    const octx = out.getContext("2d");
    const oi = octx.getImageData(0, 0, out.width, out.height);
    const d = oi.data;
    // Contrast +15% via CIE-approx linear stretch on RGB channels.
    const factor = (259 * (25 + 255)) / (255 * (259 - 25));
    for (let i = 0; i < d.length; i += 4) {
      d[i]     = Math.max(0, Math.min(255, factor * (d[i]     - 128) + 128));
      d[i + 1] = Math.max(0, Math.min(255, factor * (d[i + 1] - 128) + 128));
      d[i + 2] = Math.max(0, Math.min(255, factor * (d[i + 2] - 128) + 128));
    }
    octx.putImageData(oi, 0, 0);

    // Re-encode as JPEG @ 0.85 — good doc quality, tiny bytes.
    const blob = await new Promise((res) => out.toBlob(res, "image/jpeg", 0.85));
    // Wrap as File so the FormData upload keeps a proper filename.
    const suffix = file.name.replace(/\.[^.]+$/, "");
    return new File([blob], `${suffix}-enhanced.jpg`, { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function DocumentSlot({
  label,
  slot,
  slot4Options = null,
  onChange,       // ({ file_id, url, filename, doc_type })
  disabled = false,
  errorText = "",
  "data-testid": testId,
}) {
  const [busy, setBusy]     = useState(false);
  const [saved, setSaved]   = useState(null);   // full upload response
  const [docType, setDocType] = useState("");
  const inputRef = useRef(null);

  const upload = useCallback(async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`${label}: file exceeds 50 MB limit`);
      return;
    }
    // Enforce slot-4 doc-type BEFORE the network hit.
    if (slot === "slot4" && !docType) {
      toast.error("Please choose the Slot 4 document type before uploading");
      return;
    }
    setBusy(true);
    try {
      let toSend = file;
      // Enhance images client-side. PDFs pass through untouched.
      if (file.type.startsWith("image/")) {
        try {
          toSend = await enhanceImage(file);
        } catch (_e) {
          // If enhancement blows up (e.g. HEIC unsupported), fall back to raw.
          toSend = file;
        }
      }
      const fd = new FormData();
      fd.append("file", toSend);
      fd.append("slot", slot);
      if (slot === "slot4") fd.append("doc_type", docType);
      const { data } = await axios.post(`${API}/contractor-setup/upload-document`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSaved(data);
      onChange?.({
        file_id:  data.file_id,
        url:      data.url,
        filename: data.filename,
        doc_type: data.doc_type,
      });
      toast.success(`${label} uploaded`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [label, slot, docType, onChange]);

  return (
    <div
      className={`border rounded p-3 space-y-2 ${errorText ? "border-rose-500 bg-rose-950/20" : saved ? "border-emerald-600/40 bg-emerald-950/10" : "border-zinc-800 bg-zinc-950"}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">
          {label}
        </div>
        {saved && (
          <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0"/>
        )}
      </div>

      {slot === "slot4" && slot4Options && (
        <select
          value={docType}
          onChange={(e) => { setDocType(e.target.value); setSaved(null); }}
          disabled={busy || disabled}
          className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-100"
          data-testid={`${testId}-type`}
        >
          <option value="">— Choose supporting document —</option>
          {slot4Options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      )}

      {saved ? (
        <div className="flex items-center justify-between gap-2 bg-zinc-900 border border-zinc-700 rounded px-3 py-2">
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-300 truncate">
            <FileText className="h-3.5 w-3.5"/>
            {saved.filename}
          </span>
          <button
            type="button"
            onClick={() => { setSaved(null); onChange?.(null); }}
            disabled={busy || disabled}
            data-testid={`${testId}-remove`}
            className="text-zinc-500 hover:text-rose-400"
          >
            <X className="h-4 w-4"/>
          </button>
        </div>
      ) : (
        <label
          className={`w-full inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-amber-400/60 text-zinc-100 text-xs font-bold px-3 py-2.5 rounded cursor-pointer ${(busy || disabled) ? "opacity-60 cursor-not-allowed" : ""}`}
          data-testid={`${testId}-label`}
        >
          {busy
            ? <Loader2 className="h-4 w-4 animate-spin"/>
            : <Upload className="h-4 w-4"/>}
          <span>{busy ? "Enhancing + uploading…" : "Choose file"}</span>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT_ATTR}
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
            disabled={busy || disabled}
            data-testid={`${testId}-input`}
          />
        </label>
      )}

      <p className="text-[10px] text-zinc-500">
        JPG / PNG / HEIC / PDF · client-enhanced · ≤ 50 MB
      </p>
      {errorText && (
        <p className="text-[10px] text-rose-400" data-testid={`${testId}-error`}>{errorText}</p>
      )}
    </div>
  );
}
